// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, styledScrollbar, xScreen } from "@flip/XDL/Utilities";
import { getSystemColor } from "@flip/XDL/Utilities/color";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

export interface TippyPopupStyle {
    main?: InjectableCss;
    headerMenuMobile?: InjectableCss;
    elementWrapper?: React.CSSProperties;
    closeButton?: IconPresentationProps;
}

export const mobileTippyBreakpoint = "sm";

// we add it to `preStyle/globalStyle`
export const mobileTippyStyle = css`
    /* XNG-Changes: FLIP-662 */
    ${xScreen("lte", "xs")} {
        div[data-tippy-root] {
            width: 100%;
            max-width: none;
        }
    }
    ${xScreen("lte", mobileTippyBreakpoint)} {
        div[data-tippy-root].tippy-menu-left-side {
            width: 100%;
            max-width: 100%;
            transition: transform 150ms ease-in-out;
            top: var(--tippy-top-offset) !important;
            left: 0 !important;
            right: 0 !important;
            overflow-y: visible;
            overflow-x: hidden;
            z-index: ${getProp("theme.zIndex.modal")} !important;
            .tippy-box {
                border: none;
                box-shadow: none;
            }
        }

        .tippy-content {
            background: ${color("common.ui1")};
            padding: 0;
            & > div {
                height: 100%;
            }
        }
    }
`;

type Dir = "top" | "bottom" | "right" | "left";

const getArrowStyles = (placement: Dir, opposite: Dir) => {
    return `
        &,&:before{
            position: absolute;
            width: 16px;
            height: 16px;
            z-index: 1;
        }

        &:before{
            content: "";
            background: ${getSystemColor("common.ui1")};
            border-color: ${getSystemColor("common.border")};
            border-style: solid;
            border-width: 0 1px 1px 0;
        }

        ${
            placement === "top"
                ? `
                    bottom: -23px;
                    &:before {
                        transform: rotate(45deg);
                    }`
                : ``
        }
        
        ${
            placement === "bottom"
                ? `
                    top: -23px;
                    &:before {
                        transform: rotate(-135deg);
                    }`
                : ``
        }
        ${
            placement === "left"
                ? `
                    right: -23px;
                    &:before {
                        transform: rotate(-45deg);
                    }`
                : ``
        }
        ${
            placement === "right"
                ? `
                    left: -23px;
                    top: -23px;
                    &:before {
                        transform: rotate(135deg);
                    }`
                : ``
        }
    `;
};

export const tippyPopupStyles: TippyPopupStyle = {
    main: {
        css: css`
            &.tippy-box {
                transition-duration: 500ms !important;
                color: ${color("text.main")};
                background-color: ${color("common.ui1")};
                border: none;
                min-width: 200px;
                border-radius: 0;
                border: 1px solid ${color("common.border")};

                &:focus,
                & > *:focus {
                    outline: none;
                }

                & > .tippy-content {
                    padding: 0;
                    ${styledScrollbar(4)}
                    max-height: calc(100vh - 115px);
                    ${xScreen("lte", "xs")} {
                        max-height: calc(100vh - 190px);
                    }
                    overflow-y: auto;
                    overflow-x: hidden;
                    & > div {
                        height: 100%;
                    }
                }

                &&&[data-placement^="top"] > .tippy-arrow {
                    ${getArrowStyles("top", "bottom")}
                    &::before {
                        bottom: 1px;
                        left: -10px;
                    }
                }

                &&&[data-placement^="bottom"] > .tippy-arrow {
                    ${getArrowStyles("bottom", "top")}
                    &::before {
                        top: 1px;
                        left: -10px;
                    }
                }

                &&&[data-placement^="right"] > .tippy-arrow {
                    ${getArrowStyles("right", "left")}
                    &::before {
                        left: 1px;
                        top: -9px;
                    }
                }

                &&&[data-placement^="left"] > .tippy-arrow {
                    ${getArrowStyles("left", "right")}
                    &::before {
                        right: 1px;
                        top: -9px;
                    }
                }

                &&& .tippy-arrow,
                &&& .tippy-arrow::before {
                    position: absolute;
                    width: 0;
                    height: 0;
                    z-index: 1;
                }
            }
        `,
    },
    elementWrapper: {
        outline: "none !important",
    },
    headerMenuMobile: {
        css: css`
            background: ${color("primary")};
            position: relative;
            top: -${({ paddingTop }: any) => paddingTop}px;
            left: -${({ paddingLeft }: any) => paddingLeft}px;
            width: calc(100% + ${({ paddingLeft, paddingRight }: any) => paddingLeft + paddingRight}px);
            padding: 5px 20px;
            img {
                max-height: 48px;
            }
        `,
    },
    closeButton: {
        size: 14,
        src: "X",
        color: "common.ui5",
        css: css`
            cursor: pointer;
            position: absolute;
            right: 10px;
            top: 10px;
            z-index: 999;

            &:hover {
                color: ${color("danger")};
            }

            &.close-icon-menu {
                top: 50%;
                transform: translateY(-50%);
                & {
                    color: ${color("common.ui1")};
                    &:hover {
                        color: ${color("danger")};
                    }
                }
            }

            ${({ closeButton }: any) => {
                /** { closeButton: "both" | "mobile" | "desktop" } */

                // both
                let style: any = css`
                    display: flex;
                `;

                if (closeButton === "desktop") {
                    style = css`
                        display: flex;
                        ${xScreen("lte", mobileTippyBreakpoint)} {
                            display: none;
                        }
                    `;
                } else if (closeButton === "mobile") {
                    style = css`
                        display: none;
                        ${xScreen("lte", mobileTippyBreakpoint)} {
                            display: flex;
                        }
                    `;
                }
                return style;
            }}
        `,
    },
};
